/**
 * Password Policy — Single Source of Truth (web and Ops)
 *
 * Mirrors the Supabase Auth project settings (yodnouwloaabmhkffdpl):
 *   • Minimum length:    8 characters
 *   • Required classes:  lowercase, uppercase, digits, symbols
 *   • Leak protection:   ON (HaveIBeenPwned via Pwned Passwords API)
 *
 * Any change here must be applied at the dashboard side too — and vice-versa.
 * The frontend pre-checks length + complexity locally so users get instant
 * feedback; the leak check only runs server-side, so its rejection comes
 * back as an `AuthWeakPasswordError` and is translated by `passwordErrorMessage`.
 */

export const PASSWORD_MIN_LENGTH = 8;

/**
 * The symbols Supabase Auth accepts (`password_required_characters`, read
 * 2026-10-26): !@#$%^&*()_+-=[]{};'\:"|<>?,./`~ — iOS Strong Passwords use
 * only `-` (JOSH-76). `-` sits last so it never forms a range.
 */
const PASSWORD_SYMBOL = /[!@#$%^&*()_+=[\]{};'\\:"|<>?,./`~-]/;

export const PASSWORD_REQUIREMENTS_HINT =
  'Mínimo 8 caracteres, com letra maiúscula, minúscula, número e símbolo';

/**
 * Validate password strength against the project's policy.
 * Returns an array of human-readable failure fragments (empty array = valid).
 *
 * Used as building blocks for messages like:
 *   `A senha precisa de: ${reasons.join(', ')}.`
 */
export function validatePasswordStrength(password: string): string[] {
  const reasons: string[] = [];
  if (password.length < PASSWORD_MIN_LENGTH) {
    reasons.push(`mínimo ${PASSWORD_MIN_LENGTH} caracteres`);
  }
  if (!/[A-Z]/.test(password)) reasons.push('uma letra maiúscula');
  if (!/[a-z]/.test(password)) reasons.push('uma letra minúscula');
  if (!/[0-9]/.test(password)) reasons.push('um número');
  if (!PASSWORD_SYMBOL.test(password)) {
    reasons.push('um caractere especial (!@#$%^&*...)');
  }
  return reasons;
}

// ==================== ERROR TRANSLATION ====================

type WeakPasswordReason =
  | 'pwned'
  | 'length'
  | 'characters'
  | 'lowercase'
  | 'uppercase'
  | 'numbers'
  | 'symbols';

interface MaybeAuthError {
  code?: string;
  message?: string;
  error?: string; // Edge Function envelope shape
  /** supabase-js `AuthWeakPasswordError` carries the reasons at top level. */
  reasons?: WeakPasswordReason[];
  /** Edge Function envelope / `updateUser` response shape. */
  weakPassword?: { reasons?: WeakPasswordReason[] };
}

export const PASSWORD_PWNED_MESSAGE =
  'Esta senha já apareceu em vazamentos públicos conhecidos (de outros sites) e por isso não pode ser usada no Joshua. Escolha uma senha que você não use em nenhum outro lugar.';

export const PASSWORD_WEAK_MESSAGE =
  `A senha não atende aos requisitos: ${PASSWORD_REQUIREMENTS_HINT.toLowerCase()}.`;

export const PASSWORD_SAME_MESSAGE =
  'A nova senha precisa ser diferente da senha atual.';

/**
 * Translate a Supabase Auth password error into Portuguese.
 * Returns `null` if the error isn't password-related — caller falls
 * back to its generic error handling.
 *
 * Recognises the supabase-js direct shapes (`AuthWeakPasswordError` with
 * top-level `reasons`, `AuthApiError` with `code`) and the Edge Function
 * envelope shape forwarded by `admin-reset-password` / `admin-create-user`
 * (`{ error, code, weakPassword }`).
 */
export function passwordErrorMessage(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as MaybeAuthError;

  const code = e.code;
  const raw = (e.message ?? e.error ?? '').toLowerCase();
  const reasons = [
    ...(Array.isArray(e.reasons) ? e.reasons : []),
    ...(e.weakPassword?.reasons ?? []),
  ];

  if (code === 'same_password' || /should be different from the old/.test(raw)) {
    return PASSWORD_SAME_MESSAGE;
  }

  const isWeakPassword =
    code === 'weak_password' ||
    /weak.?password|public data breach|pwned|leaked|known to be weak/.test(raw);
  if (!isWeakPassword) return null;

  if (reasons.includes('pwned') || /breach|pwned|leaked|known to be weak/.test(raw)) {
    return PASSWORD_PWNED_MESSAGE;
  }
  // Length / character-class failures: surface the same hint we use elsewhere.
  return PASSWORD_WEAK_MESSAGE;
}
